import { ContentFile } from '@web/vin-sessions/types';

export interface DocumentUploadDetails {
    fileName: string;
    fileType?: string;
    uploadDate?: string;
    uploadBy?: string;
}

export const readDocumentUploadDetails = (data: unknown): DocumentUploadDetails | undefined => {
    if (!data || typeof data !== 'object') {
        return undefined;
    }
    const { documentUploadDetails } = data as { documentUploadDetails?: Partial<DocumentUploadDetails> | null };
    return documentUploadDetails?.fileName ? (documentUploadDetails as DocumentUploadDetails) : undefined;
};

/**
 * Prefers the poll response's upload details and falls back to the file as the browser sent it.
 *
 * @param data the poll response body
 * @param sentFile the file as the browser sent it, if this page sent it
 * @param findUploaderName looks up a display name for an account id
 * @returns the content file, or undefined when neither source has one
 */
export const resolveUploadedContentFile = async (
    data: unknown,
    sentFile: ContentFile | undefined,
    findUploaderName: (accountId: string) => Promise<string | undefined>
): Promise<ContentFile | undefined> => {
    const details = readDocumentUploadDetails(data);
    if (!details) {
        return sentFile;
    }

    let createdBy = sentFile?.createdBy;
    if (details.uploadBy) {
        try {
            createdBy = (await findUploaderName(details.uploadBy)) ?? createdBy;
        } catch {
            // The name is only for display, so keep the one we already have
        }
    }

    return {
        fileName: details.fileName,
        fileType: details.fileType || 'PDF',
        createdDate: details.uploadDate ?? sentFile?.createdDate ?? '',
        createdTimestamp: details.uploadDate ?? sentFile?.createdTimestamp ?? '',
        ...(createdBy ? { createdBy } : {}),
    };
};

const DOCX_MEDIA_TYPE = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

export const DOCUMENT_ACCEPT_TYPES = [
    'application/pdf',
    'application/msword',
    DOCX_MEDIA_TYPE,
    '.pdf',
    '.doc',
    '.docx',
];

export const ZIP_ACCEPT_TYPES = ['application/zip', 'application/x-zip-compressed', '.zip'];

const fileExtension = (fileName: string): string | undefined => {
    const name = fileName.toLowerCase();
    return name.includes('.') ? name.split('.').pop() : undefined;
};

export const toUploadFileType = (file: File): string => {
    const extension = fileExtension(file.name);
    if (file.type === 'application/pdf' || extension === 'pdf') {
        return 'PDF';
    }
    if (
        file.type === 'application/msword' ||
        file.type === DOCX_MEDIA_TYPE ||
        extension === 'doc' ||
        extension === 'docx'
    ) {
        return 'DOCX';
    }
    if (file.type === 'application/zip' || file.type === 'application/x-zip-compressed' || extension === 'zip') {
        return 'ZIP';
    }
    return file.type;
};

export type ContentFilePreview = 'pdf' | 'manual' | 'download';

export const getContentFilePreview = (
    contentFile?: ContentFile,
    isServiceDiagnostics = false
): ContentFilePreview | null => {
    if (!contentFile?.fileName) {
        return null;
    }
    const extension = fileExtension(contentFile.fileName);
    if (extension === 'pdf') {
        return 'pdf';
    }
    if (extension === 'zip') {
        return 'manual';
    }
    if (extension === 'doc' || extension === 'docx') {
        return 'download';
    }
    if (isServiceDiagnostics) {
        return 'manual';
    }
    const fileType = contentFile.fileType?.toUpperCase();
    if (fileType === 'PDF') {
        return 'pdf';
    }
    return fileType === 'ZIP' ? 'manual' : 'download';
};

export const CONTENT_FILE_PREVIEW_LABELS: Record<ContentFilePreview, { key: string; defaultLabel: string }> = {
    pdf: { key: 'stisPublications:documentInfo.fileActions.preview', defaultLabel: 'Preview' },
    manual: { key: 'stisPublications:documentInfo.fileActions.openManual', defaultLabel: 'Open manual' },
    download: { key: 'stisPublications:documentInfo.fileActions.download', defaultLabel: 'Download' },
};

export const downloadFromUrl = (url: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    link.remove();
};
